// src/ui/screens/FirebaseNewChatScreen.jsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { UserAvatar } from '../../components/UserAvatar';
import { userProfileService } from '../../services/user/userProfileService';
import { chatService } from '../../services/chat/chatService';
import { db } from '../../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';

const COLORS = {
  orange: '#FF8C08',
  bg: '#08090B',
  card: '#23262C',
};

const SEARCH_BAR_HEIGHT = 60;

// ── Phone Contact Model ─────────────────────────────────────────────────────

class PhoneContact {
  constructor({ displayName, phone, matchKey }) {
    this.displayName = displayName;
    this.phone = phone;
    this.matchKey = matchKey;
  }
}

// ── FirebaseNewChatScreen ──────────────────────────────────────────────────

export const FirebaseNewChatScreen = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [filatomUsers, setFilatomUsers] = useState([]);
  const [filteredFilatomUsers, setFilteredFilatomUsers] = useState([]);
  const [nonFilatomContacts, setNonFilatomContacts] = useState([]);
  const [filteredNonFilatomContacts, setFilteredNonFilatomContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchBarProgress, setSearchBarProgress] = useState(0);

  const scrollRef = useRef(null);
  const searchInputRef = useRef(null);

  const INVITE_MESSAGE =
    "Hey, Let's chat on FilaChat! It's fast, simple, secure messaging " +
    "subsidiary of Filatom, Uganda's #1 Real Estate App.";

  // ── Phone helpers ─────────────────────────────────────────────────────────

  const phoneMatchKey = (raw) => {
    const digits = raw.replace(/\D/g, '');
    if (digits.length >= 9) {
      return digits.substring(digits.length - 9);
    }
    return digits;
  };

  const formatPhoneForSms = (raw) => {
    let digits = raw.replace(/\D/g, '');

    // Uganda local numbers
    if (digits.startsWith('0') && digits.length === 10) {
      digits = `256${digits.substring(1)}`;
    }

    // Already has Uganda code without +
    if (digits.startsWith('256') && digits.length === 12) {
      return `+${digits}`;
    }

    // Generic international fallback
    if (!digits.startsWith('+')) {
      return `+${digits}`;
    }

    return digits;
  };

  // ── Load contacts ─────────────────────────────────────────────────────────

  useEffect(() => {
    const loadContacts = async () => {
      setIsLoading(true);
      setError('');

      try {
        const currentUid = user?.uid;

        // Check if Contacts API is available
        if (!navigator.contacts) {
          // Fallback: load all users
          const snap = await getDocs(collection(db, 'users'));
          const users = snap.docs
            .filter(doc => doc.id !== currentUid)
            .map(doc => userProfileService.UserProfile.fromMap(doc.id, doc.data()))
            .sort((a, b) => a.displayName.localeCompare(b.displayName));

          setFilatomUsers(users);
          setFilteredFilatomUsers(users);
          setNonFilatomContacts([]);
          setFilteredNonFilatomContacts([]);
          setIsLoading(false);
          return;
        }

        // Request contacts permission
        let deviceContacts = [];
        try {
          deviceContacts = await navigator.contacts.select(['name', 'tel'], { multiple: true });
        } catch (permError) {
          console.error('Contacts permission error:', permError);
          // Fallback: load all users
          const snap = await getDocs(collection(db, 'users'));
          const users = snap.docs
            .filter(doc => doc.id !== currentUid)
            .map(doc => userProfileService.UserProfile.fromMap(doc.id, doc.data()))
            .sort((a, b) => a.displayName.localeCompare(b.displayName));

          setFilatomUsers(users);
          setFilteredFilatomUsers(users);
          setNonFilatomContacts([]);
          setFilteredNonFilatomContacts([]);
          setIsLoading(false);
          return;
        }

        // Build device phone map
        const devicePhoneToContact = {};
        for (const c of deviceContacts) {
          const name = c.name?.[0] || 'Unknown';
          for (const tel of c.tel || []) {
            const matchKey = phoneMatchKey(tel);
            if (matchKey) {
              devicePhoneToContact[matchKey] = new PhoneContact({
                displayName: name,
                phone: formatPhoneForSms(tel),
                matchKey: matchKey,
              });
            }
          }
        }

        const devicePhoneSet = new Set(Object.keys(devicePhoneToContact));

        // Fetch Firestore users
        const snap = await getDocs(collection(db, 'users'));
        const allFirestoreUsers = snap.docs
          .filter(doc => doc.id !== currentUid)
          .map(doc => userProfileService.UserProfile.fromMap(doc.id, doc.data()));

        // Match users
        const matchedPhones = new Set();
        const matchedUsers = [];

        for (const userProfile of allFirestoreUsers) {
          const matchKey = phoneMatchKey(userProfile.phone);
          if (matchKey && devicePhoneSet.has(matchKey)) {
            matchedUsers.push(userProfile);
            matchedPhones.add(matchKey);
          }
        }

        matchedUsers.sort((a, b) => a.displayName.localeCompare(b.displayName));

        // Non-Filatom contacts
        const nonFilatom = [];
        for (const entry of Object.entries(devicePhoneToContact)) {
          if (!matchedPhones.has(entry[0])) {
            nonFilatom.push(entry[1]);
          }
        }
        nonFilatom.sort((a, b) => a.displayName.localeCompare(b.displayName));

        setFilatomUsers(matchedUsers);
        setFilteredFilatomUsers(matchedUsers);
        setNonFilatomContacts(nonFilatom);
        setFilteredNonFilatomContacts(nonFilatom);
      } catch (error) {
        console.error('Load contacts error:', error);
        setError(error.message || 'Failed to load contacts');
      } finally {
        setIsLoading(false);
      }
    };

    loadContacts();
  }, [user]);

  // ── Search handler ─────────────────────────────────────────────────────────

  useEffect(() => {
    const queryLower = searchQuery.toLowerCase();
    const filteredFilatom = filatomUsers.filter(u =>
      u.displayName.toLowerCase().includes(queryLower) ||
      u.phone.toLowerCase().includes(queryLower) ||
      u.username.toLowerCase().includes(queryLower)
    );
    setFilteredFilatomUsers(filteredFilatom);

    const filteredNonFilatom = nonFilatomContacts.filter(c =>
      c.displayName.toLowerCase().includes(queryLower) ||
      c.phone.includes(searchQuery)
    );
    setFilteredNonFilatomContacts(filteredNonFilatom);
  }, [searchQuery, filatomUsers, nonFilatomContacts]);

  // ── Scroll handler ─────────────────────────────────────────────────────────

  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return;
    const offset = scrollRef.current.scrollTop;
    const progress = Math.min(offset / SEARCH_BAR_HEIGHT, 1);
    setSearchBarProgress(progress);
  }, []);

  // ── Navigate to chat ──────────────────────────────────────────────────────

  const navigateToChat = (userProfile) => {
    const currentUserId = user?.uid;
    if (!currentUserId) return;

    const conversationId = [currentUserId, userProfile.uid].sort().join('-');

    navigate(`/chat/${conversationId}`, {
      state: {
        receiverId: userProfile.uid,
        title: userProfile.displayName,
        avatarUrl: userProfile.hasProfileImage ? userProfile.profileImage : null,
      },
    });
  };

  // ── Invite via SMS ────────────────────────────────────────────────────────

  const inviteViaSms = (contact) => {
    const encoded = encodeURIComponent(INVITE_MESSAGE);
    window.location.href = `sms:${contact.phone}?body=${encoded}`;
  };

  // ── Group helpers ─────────────────────────────────────────────────────────

  const groupByAlphabet = (items, getName) => {
    const grouped = {};
    for (const item of items) {
      const name = getName(item);
      const letter = name ? name[0].toUpperCase() : '#';
      if (!grouped[letter]) grouped[letter] = [];
      grouped[letter].push(item);
    }
    const keys = Object.keys(grouped).sort();
    const result = {};
    for (const key of keys) {
      result[key] = grouped[key];
    }
    return result;
  };

  // ── Render section banner ─────────────────────────────────────────────────

  const renderSectionBanner = (text) => {
    return (
      <div className="w-full px-4 py-3.5 pb-1.5 bg-filatom-bg">
        <span className="text-filatom-primary text-sm font-bold tracking-wide">
          {text}
        </span>
      </div>
    );
  };

  // ── Render section header ─────────────────────────────────────────────────

  const renderSectionHeader = (text) => {
    return (
      <div className="w-full px-4 py-1 bg-[#1A1A1A]">
        <span className="text-gray-400 text-sm font-semibold">{text}</span>
      </div>
    );
  };

  // ── Render non-Filatom item ──────────────────────────────────────────────

  const renderNonFilatomItem = (contact) => {
    return (
      <div className="w-full">
        <div className="flex items-center px-4 py-2 hover:bg-filatom-card-hover transition-colors">
          <div
            className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: COLORS.card }}
          >
            <span className="text-white font-semibold text-sm">
              {contact.displayName ? contact.displayName[0].toUpperCase() : '?'}
            </span>
          </div>
          <div className="flex-1 ml-3 min-w-0">
            <p className="text-white text-sm font-medium truncate">
              {contact.displayName}
            </p>
            <p className="text-gray-400 text-xs truncate">{contact.phone}</p>
          </div>
          <button
            onClick={() => inviteViaSms(contact)}
            className="px-3.5 py-1.5 rounded-full text-white text-xs font-semibold flex-shrink-0"
            style={{ backgroundColor: COLORS.orange }}
          >
            Invite
          </button>
        </div>
        <div className="ml-[72px] h-px bg-white/5" />
      </div>
    );
  };

  // ── Render action item ────────────────────────────────────────────────────

  const renderActionItem = (icon, title, onTap) => {
    return (
      <div className="w-full">
        <button
          onClick={onTap}
          className="w-full flex items-center px-4 py-3 hover:bg-filatom-card-hover transition-colors"
        >
          <svg className="w-7 h-7 text-filatom-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {icon === 'person_add' && (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            )}
          </svg>
          <span className="text-filatom-primary text-lg ml-3">{title}</span>
        </button>
        <div className="ml-[72px] h-px bg-white/5" />
      </div>
    );
  };

  // ── Build contact slivers ──────────────────────────────────────────────────

  const buildContactSlivers = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <div className="w-8 h-8 border-2 border-filatom-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-400 text-sm">Finding your contacts on Filatom...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-center justify-center h-64">
          <p className="text-red-500 text-sm">Error: {error}</p>
        </div>
      );
    }

    if (filteredFilatomUsers.length === 0 && filteredNonFilatomContacts.length === 0) {
      return (
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-400 text-sm">No contacts found</p>
        </div>
      );
    }

    const elements = [];

    // Filatom users
    if (filteredFilatomUsers.length > 0) {
      elements.push(renderSectionBanner('People you may know'));

      const grouped = groupByAlphabet(filteredFilatomUsers, u => u.displayName);
      for (const [letter, users] of Object.entries(grouped)) {
        elements.push(renderSectionHeader(letter));
        for (const userProfile of users) {
          elements.push(
            <div key={userProfile.uid}>
              <button
                onClick={() => navigateToChat(userProfile)}
                className="w-full flex items-center px-4 py-2 hover:bg-filatom-card-hover transition-colors"
              >
                <UserAvatar
                  size={44}
                  imageUrl={userProfile.hasProfileImage ? userProfile.profileImage : null}
                  name={userProfile.displayName}
                />
                <div className="flex-1 ml-3 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{userProfile.displayName}</p>
                  <p className="text-gray-400 text-xs truncate">{userProfile.phone}</p>
                </div>
              </button>
              <div className="ml-[72px] h-px bg-white/5" />
            </div>
          );
        }
      }
    }

    // Invite to Filatom
    if (filteredNonFilatomContacts.length > 0) {
      elements.push(renderSectionBanner('Invite to Filatom'));

      const grouped = groupByAlphabet(filteredNonFilatomContacts, c => c.displayName);
      for (const [letter, contacts] of Object.entries(grouped)) {
        elements.push(renderSectionHeader(letter));
        for (const contact of contacts) {
          elements.push(renderNonFilatomItem(contact));
        }
      }
    }

    return elements;
  };

  // ── Main render ───────────────────────────────────────────────────────────

  const statusBarHeight = 44;
  const headerHeight = statusBarHeight + 56 + SEARCH_BAR_HEIGHT + 8;
  const searchBarOffset = SEARCH_BAR_HEIGHT * searchBarProgress;
  const gradientHeight = headerHeight - searchBarOffset;

  return (
    <div className="min-h-screen bg-filatom-bg">
      {/* Content */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="h-screen overflow-y-auto"
        style={{ paddingTop: headerHeight, paddingBottom: 32 }}
      >
        {/* New Contact Action */}
        {renderActionItem('person_add', 'New contact', () => navigate('/new-contact'))}

        {/* Contacts */}
        {buildContactSlivers()}
      </div>

      {/* Gradient Overlay */}
      <div
        className="absolute top-0 left-0 right-0 pointer-events-none"
        style={{
          height: gradientHeight,
          background: `linear-gradient(to bottom, rgba(8,9,11,0.95) 0%, rgba(8,9,11,0.75) 60%, rgba(8,9,11,0) 100%)`,
        }}
      />

      {/* Floating Header */}
      <div className="absolute top-0 left-0 right-0">
        {/* Top Bar */}
        <div className="flex items-center justify-between px-4 h-14" style={{ paddingTop: statusBarHeight }}>
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-full flex items-center justify-center"
            style={{ backgroundColor: '#0F1013' }}
          >
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <span className="text-white text-lg font-bold">New chat</span>
          <div className="w-9" />
        </div>

        {/* Search Bar - slides up with scroll */}
        <div
          className="px-4 py-2 transition-all duration-200"
          style={{
            transform: `translateY(-${searchBarOffset}px)`,
            opacity: 1 - searchBarProgress,
          }}
        >
          <div
            className="relative h-11 rounded-full overflow-hidden"
            style={{ backgroundColor: COLORS.card }}
          >
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-full bg-transparent text-white text-sm outline-none px-3"
              placeholder=""
            />
            {!searchQuery && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="flex items-center gap-1.5">
                  <svg className="w-4.5 h-4.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <span className="text-gray-400 text-sm">Search</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FirebaseNewChatScreen;